import { completeProfilePromptCompletedAtom, isLoggedInAtom, userInfoAtom } from 'app/atoms';
import { useNavigation } from 'app/hooks/navigation';
import { useAllAddresses } from 'app/network/address';
import { useCountUnreadMessages } from 'app/network/chat/message';
import { useAllInterests } from 'app/network/interests';
import { LoanFilterKey, LoanType, useAllLoans } from 'app/network/loan';
import { useMyProfile } from 'app/network/profile';
import { useTrialFreeSubAvailable } from 'app/network/promotions';
import { useStatistics } from 'app/network/statistics';
import { useUser } from 'app/network/user';
import { optimisticUserRecentSearchesUpdate } from 'app/hooks/use-search';
import { useQueryKeys } from 'app/network/invalidation';
import { ListingType } from 'app/types/listing';
import { useInviteOthersPrompt } from 'app/screens/billboard/all-listings/components/invite-others-prompt';
import { useShowPromptPhoneNumber } from 'app/components/prompt-phone-number-banner';
import { useFocusEffect } from '@react-navigation/native';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { canUseInterests, DynamicFeatureFlag, FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { navigateToAllListings } from '../navigator-helper';
import { useVisibleExpiredLoans } from '../all-listings/components/expired-loans';
import { useBonificationBanner } from '../all-listings/components/bonification-banner';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';

const DEFAULT_PROFILE_AVATAR_PATH = '/default/media/avatar.png';

export function useLandingPage() {
  const { t } = useTranslation('landing-page');
  const { t: tRecommendations } = useTranslation('recommendations');
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const navigation = useNavigation<'landing-page'>();
  const { visibleExpiredLoans, hideLoan: hideExpiredLoan } = useVisibleExpiredLoans();
  const bonificationBanner = useBonificationBanner();

  // ── Data fetching ────────────────────────────────────────────────────────────
  const { data: profile, isPending: isProfilePending } = useMyProfile();
  const { data: user, isPending: isUserPending } = useUser();
  const canViewActivitySummary =
    !isUserPending && isFeatureEnabled(DynamicFeatureFlag.ActivitySummary, user?.featureFlags);
  const { data: addresses, isPending: isAddressesPending } = useAllAddresses();
  const { data: unreadData, isPending: isUnreadPending } = useCountUnreadMessages();
  const { data: stats, isPending: isStatsPending } = useStatistics(user?.id ?? '', { enabled: !!user?.id });

  const [deferLoanQueries, setDeferLoanQueries] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (!cancelled) setDeferLoanQueries(true);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, []);

  const { data: lendings, isFetched: isLendingsFetched } = useAllLoans(LoanFilterKey.Management, LoanType.Lending, {
    enabled: deferLoanQueries,
  });
  const { data: borrowings, isFetched: isBorrowingsFetched } = useAllLoans(
    LoanFilterKey.Management,
    LoanType.Borrowing,
    { enabled: deferLoanQueries },
  );
  const totalLendings = lendings?.length;
  const totalBorrowings = borrowings?.length;
  const loansQuerySettled = deferLoanQueries && isLendingsFetched && isBorrowingsFetched;
  const { freeSubAvailable, freeSubNumDaysLeft } = useTrialFreeSubAvailable()();
  const interestsAvailable = canUseInterests();
  const {
    data: userInterests,
    refetch: refetchUserInterests,
    isPending: isInterestsPending,
  } = useAllInterests({ enabled: interestsAvailable });
  const userInfo = useAtomValue(userInfoAtom);

  useRegisterLoading(isProfilePending, 'LandingPageScreen-profile');
  useRegisterLoading(isUserPending, 'LandingPageScreen-user');
  useRegisterLoading(isAddressesPending, 'LandingPageScreen-addresses');
  useRegisterLoading(isUnreadPending, 'LandingPageScreen-unread');
  useRegisterLoading(isStatsPending && !!user?.id, 'LandingPageScreen-statistics');
  useRegisterLoading(interestsAvailable && isInterestsPending, 'LandingPageScreen-interests');

  // ── State ────────────────────────────────────────────────────────────────────
  const [completeProfilePromptCompleted, setCompleteProfilePromptCompleted] = useAtom(
    completeProfilePromptCompletedAtom,
  );
  const [isSearchOverlayVisible, setIsSearchOverlayVisible] = useState(false);
  const canPublish = isFeatureEnabled(FeatureFlag.publishListings, userInfo?.featureFlags);

  // ── Effects ──────────────────────────────────────────────────────────────────
  useFocusEffect(() => {
    if (isLoggedIn && interestsAvailable) void refetchUserInterests();
  });

  // ── Derived values ───────────────────────────────────────────────────────────
  const showInviteOthersPrompt = useInviteOthersPrompt();
  const showPromptPhoneNumber = useShowPromptPhoneNumber();
  const hasCustomProfilePicture = Boolean(profile?.picture && !profile.picture.endsWith(DEFAULT_PROFILE_AVATAR_PATH));
  const primaryAddress = addresses?.[0];
  const unreadCount = unreadData?.unread_count ?? 0;

  const activitySubtitle = (() => {
    const parts: string[] = [];
    if ((totalLendings ?? 0) > 0) parts.push(t('quickActions.loanOutgoing', { count: totalLendings }));
    if ((totalBorrowings ?? 0) > 0) parts.push(t('quickActions.loanIncoming', { count: totalBorrowings }));
    if (parts.length > 0) return parts.join(' · ');
    if (!loansQuerySettled) return '';
    return t('quickActions.noActivity');
  })();

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greeting.morning');
    if (hour < 18) return t('greeting.afternoon');
    return t('greeting.evening');
  })();

  const addressLabel = (() => {
    if (!primaryAddress?.postalCode) return '';
    const parts = [primaryAddress.postalCode.toUpperCase(), primaryAddress.city].filter(Boolean);
    return parts.length > 0 ? parts.join(' - ') : '';
  })();

  // ── Callbacks ────────────────────────────────────────────────────────────────
  const qk = useQueryKeys();
  const client = useQueryClient();

  const handleSearchSubmit = (query: string) => {
    setIsSearchOverlayVisible(false);
    if (query.length > 0) {
      optimisticUserRecentSearchesUpdate(client, qk, query);
    }
    navigateToAllListings(navigation, { type: ListingType.offer, search: query });
  };

  const handleTagPress = (tagId: string) => {
    setIsSearchOverlayVisible(false);
    navigateToAllListings(navigation, { tagId, type: ListingType.offer });
  };

  const handleCategoryPress = (categoryId: string) => {
    setIsSearchOverlayVisible(false);
    navigateToAllListings(navigation, { categoryId, search: '', tagId: undefined, type: ListingType.offer });
  };

  const handleViewAllListings = () => {
    setIsSearchOverlayVisible(false);
    navigateToAllListings(navigation, { type: ListingType.offer, search: '' });
  };

  return {
    // navigation
    navigation,
    // data
    profile,
    user,
    stats,
    userInterests,
    interestsAvailable,
    unreadData,
    // derived
    isLoggedIn,
    freeSubAvailable,
    freeSubNumDaysLeft,
    hasCustomProfilePicture,
    showInviteOthersPrompt,
    showPromptPhoneNumber,
    unreadCount,
    activitySubtitle,
    greeting,
    addressLabel,
    totalLendings,
    totalBorrowings,
    lendings,
    borrowings,
    loansQuerySettled,
    visibleExpiredLoans,
    hideExpiredLoan,
    bonificationBanner,
    // state
    isSearchOverlayVisible,
    setIsSearchOverlayVisible,
    completeProfilePromptCompleted,
    setCompleteProfilePromptCompleted,
    canPublish,
    canViewActivitySummary,
    // handlers
    handleSearchSubmit,
    handleTagPress,
    handleCategoryPress,
    handleViewAllListings,
    // translations
    t,
    tRecommendations,
  };
}
